import { CompositeLayerProps, Filter } from '@Map/layers/types';
import type VectorTileLayer from '@arcgis/core/layers/VectorTileLayer';

import LayerBase from './LayerBase';

export default class MainLayer extends LayerBase {
	protected _selectedItems: string[] = [];

	constructor(
		layerInfo: CompositeLayerProps,
		vectorTileLayer: VectorTileLayer | undefined,
		composite?: import('./LayerBase').CompositeLike,
	) {
		super(layerInfo, vectorTileLayer, {
			zIndexesPerType: { line: -4, fill: -5 },
			composite,
		});
		this.filter = MainLayer.getFilter(
			layerInfo.properties,
			false,
			undefined,
		);
	}

	set selectedItems(items: string[]) {
		const removed = this._selectedItems.filter(
			item => !items.includes(item),
		);
		this._stateDeselect(removed);
		this._selectedItems = items;
		this._stateSelect();
	}

	static getFilter(
		compositeProps: CompositeLayerProps['properties'],
		hasClustering: boolean,
		filter: Filter | undefined,
	): Filter | undefined {
		if (filter) return filter;
		const properties = Object.entries(compositeProps ?? {});
		if (properties.length) {
			const propsFilter: Filter[] = ['all'];
			properties.forEach(([key, value]) => {
				propsFilter.push(MainLayer._mapValueToFilter(key, value));
			});
			if (hasClustering) {
				return ['all', ['!', ['has', 'point_count']], propsFilter];
			}
			return propsFilter;
		} else if (hasClustering) {
			return ['!', ['has', 'point_count']];
		}
		return undefined;
	}

	static _mapValueToFilter(
		key: string,
		value: string | string[] | [number, number],
	): Filter {
		if (Array.isArray(value)) {
			if (typeof value[0] === 'number' && typeof value[1] === 'number') {
				const min = ['>=', ['get', key], value[0]];
				const max = ['<=', ['get', key], value[1]];
				return ['all', min, max];
			} else if (value.length === 0) {
				return ['==', 1, 0];
			} else {
				return ['in', key, ...value];
			}
		} else {
			return ['==', ['get', key], value];
		}
	}

	protected _getFilter = (): Filter | undefined => {
		const filter = MainLayer.getFilter(
			this.properties,
			this.hasClustering,
			this.filter,
		);

		return filter;
	};

	protected _resetState = (): void => {
		this._stateSelect();
	};

	protected _stateSelect(): void {
		this._selectedItems.forEach(item => {
			this.setState(item, { selected: true });
		});
	}

	protected _stateDeselect(items: string[]): void {
		items.forEach(item => {
			this.setState(item, { selected: false });
		});
	}

	protected setState(id: string, state = {}): void {}
}
